import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import DashboardLayout from "@/layouts/DashboardLayout";
import Header from "@/components/ui/header";
import PageHeader from "@/components/ui/page-header";
import Table from "@/components/ui/table";
import Popover from "@/components/ui/popover";
import ConfirmModal from "@/components/ui/confirm-modal";
import {
  Actions as ActionsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Department as CompanyIcon,
} from "@/components/icons";
import type { TableColumn } from "@/types/component";
import type {
  Company,
  CreateCompanyPayload,
} from "../types/companies.types";
import { companiesApi } from "../services/companies.api";
import CompanyModal from "../components/CompanyModal";
import { usePermission } from "@/core/permissions/hooks";
import { PERMISSIONS } from "@/core/permissions/config";
import styles from "./CompaniesPage.module.scss";

const CompaniesPage: React.FC = () => {
  const { t } = useTranslation();
  const { hasPermission } = usePermission();
  const canCreate = hasPermission(PERMISSIONS.COMPANIES_CREATE);
  const canUpdate = hasPermission(PERMISSIONS.COMPANIES_UPDATE);
  const canDelete = hasPermission(PERMISSIONS.COMPANIES_DELETE);

  const [companies, setCompanies] = useState<Company[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [openDropdowns, setOpenDropdowns] = useState<Record<string, boolean>>(
    {},
  );
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [deleteTarget, setDeleteTarget] = useState<Company | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [editTarget, setEditTarget] = useState<Company | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);

  const fetchCompanies = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await companiesApi.getList({
        page: currentPage,
        limit: pageSize,
        search: debouncedSearchTerm.trim() || undefined,
      });
      setCompanies(res.data);
      setTotalRecords(res.total);
    } catch {
      setCompanies([]);
      setTotalRecords(0);
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, pageSize, debouncedSearchTerm]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  const handleDropdownToggle = (rowId: string, isOpen: boolean) => {
    setOpenDropdowns((prev) => ({ ...prev, [rowId]: isOpen }));
  };

  const handleAdd = async (payload: CreateCompanyPayload) => {
    try {
      await companiesApi.create(payload);
      toast.success(t("companies.createSuccess"));
      setIsModalOpen(false);
      fetchCompanies();
    } catch {
      /* toast handled by interceptor */
    }
  };

  const handleEditClick = (row: Company) => {
    setEditTarget(row);
    setIsEditModalOpen(true);
    handleDropdownToggle(row.id, false);
  };

  const handleUpdate = async (payload: CreateCompanyPayload) => {
    if (!editTarget) return;
    try {
      await companiesApi.update(editTarget.id, payload);
      toast.success(t("companies.updateSuccess"));
      setEditTarget(null);
      setIsEditModalOpen(false);
      fetchCompanies();
    } catch {
      /* toast handled by interceptor */
    }
  };

  const handleDeleteClick = (row: Company) => {
    setDeleteTarget(row);
    handleDropdownToggle(row.id, false);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setIsDeleting(true);
    try {
      await companiesApi.delete(deleteTarget.id);
      toast.success(t("companies.deleteSuccess"));
      setDeleteTarget(null);
      fetchCompanies();
    } catch {
      /* toast handled by interceptor */
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: TableColumn<Company>[] = [
    {
      header: t("companies.columns.name"),
      accessor: (row) => (
        <span className={styles.nameCell}>
          <span className={styles.iconWrap}>
            <CompanyIcon width={16} height={16} />
          </span>
          <span className={styles.companyName}>{row.name}</span>
        </span>
      ),
      className: styles.nameColumn,
    },
    {
      header: t("companies.columns.code"),
      accessor: (row) =>
        row.code ? (
          <span className={styles.codeBadge}>{row.code}</span>
        ) : (
          "—"
        ),
      className: styles.codeColumn,
    },
    {
      header: t("companies.columns.users"),
      accessor: (row) =>
        row.userCount && row.userCount > 0 ? (
          <span className={styles.userCountBadge}>{row.userCount}</span>
        ) : (
          "—"
        ),
      className: styles.usersColumn,
    },
  ];

  const renderActions = (row: Company) => {
    if (!canUpdate && !canDelete) return null;
    const rowId = row.id;
    const isOpen = openDropdowns[rowId] || false;
    const menuContent = (
      <div className={styles.actionMenu}>
        {canUpdate && (
          <button
            className={styles.actionMenuItem}
            onClick={(e) => {
              e.stopPropagation();
              handleEditClick(row);
            }}
          >
            <EditIcon width={20} height={20} />
            {t("buttons.edit")}
          </button>
        )}
        {canDelete && (
          <button
            className={`${styles.actionMenuItem} ${styles.error}`}
            onClick={(e) => {
              e.stopPropagation();
              handleDeleteClick(row);
            }}
          >
            <DeleteIcon width={20} height={20} />
            {t("buttons.delete")}
          </button>
        )}
      </div>
    );
    return (
      <Popover
        content={menuContent}
        isOpen={isOpen}
        onClose={() => handleDropdownToggle(rowId, false)}
        position="bottom"
      >
        <button
          className={styles.actionButton}
          onClick={(e) => {
            e.stopPropagation();
            handleDropdownToggle(rowId, !isOpen);
          }}
        >
          <ActionsIcon width={20} height={20} />
        </button>
      </Popover>
    );
  };

  return (
    <DashboardLayout>
      <Header title={t("companies.title")} />
      <PageHeader
        searchPlaceholder={t("companies.searchPlaceholder")}
        searchValue={searchTerm}
        onSearch={setSearchTerm}
        onAddNew={canCreate ? () => setIsModalOpen(true) : undefined}
        addButtonLabel={
          canCreate ? t("companies.addButton") : undefined
        }
      />
      <Table
        columns={columns}
        data={companies}
        isLoading={isLoading}
        getRowId={(row) => row.id}
        totalRecords={totalRecords}
        currentPage={currentPage}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setCurrentPage(1);
        }}
        renderActions={renderActions}
        openDropdowns={openDropdowns}
        emptyMessage={t("companies.noData")}
      />
      <CompanyModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleAdd}
      />
      <CompanyModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditTarget(null);
        }}
        onSubmit={handleUpdate}
        editCompany={editTarget}
        title={t("companies.editTitle")}
      />
      <ConfirmModal
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
        title={t("companies.deleteConfirmTitle")}
        message={
          deleteTarget
            ? t(
                "companies.deleteConfirmMessage",
                `"{{name}}" şirketini silmek istediğinize emin misiniz?`,
                { name: deleteTarget.name },
              )
            : ""
        }
        confirmText={t("buttons.delete")}
        loading={isDeleting}
        confirmVariant="danger"
      />
    </DashboardLayout>
  );
};

export default CompaniesPage;
